'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    className?: string;
    children: ReactNode;
}

const sizeMap: Record<ButtonSize, string> = {
    sm: 'px-2.5 py-1 text-xs',
    md: 'px-3.5 py-2 text-sm',
    lg: 'px-4 py-2.5 text-base',
};

const variantMap: Record<ButtonVariant, string> = {
    primary:   'bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950 shadow-sm',
    secondary: 'bg-white text-slate-800 border border-slate-300 hover:bg-slate-50 active:bg-slate-100',
    danger:    'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
    success:   'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm',
    ghost:     'text-slate-700 hover:bg-slate-100 active:bg-slate-200',
};

/**
 * Generic button dengan palette Notion (Decision 8).
 */
export default function Button({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    leftIcon = null,
    rightIcon = null,
    type = 'button',
    className,
    children,
    ...rest
}: ButtonProps) {
    const base =
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60';

    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={clsx(base, sizeMap[size], variantMap[variant], className)}
            {...rest}
        >
            {loading && (
                <span
                    aria-hidden="true"
                    className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin"
                />
            )}
            {!loading && leftIcon}
            <span>{children}</span>
            {!loading && rightIcon}
        </button>
    );
}
